import { Layout as AntLayout, Breadcrumb, Button, Descriptions, Dropdown, Menu, MenuProps, Modal, PageHeader, Space, Spin } from 'antd';
import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, NavLink } from 'react-router-dom';
import { Dispatch } from 'redux';
import { aggregatorsListAction } from '~Aggregators/actions';
import IAggregator from '~Api/Aggregator/IAggregator';
import { aggregatorsSelector } from '~Aggregators/selectors';
import ApprovalStatusEnum from '~Api/Broker/ApprovalStatusEnum';
import IBroker from '~Api/Broker/IBroker';
import {
    brokerApproveAction,
    brokerResetApprovalStatusAction,
} from '~Brokers/actions';
import NextContact from '~Brokers/NextContact';
import { brokerSelector } from '~Brokers/selectors';
import { IGlobalState } from '~reducer';
import { IDictionary } from '~utilities/IDictionary';
import BdmSelector from './BdmSelector';
import './broker.less';
import RejectModal from './RejectModal';
import RecordContactModal from './RecordContactModal';
import { ItemType } from 'antd/lib/menu/hooks/useItems';
import menuDivider from '~UI/menuDivider';
import dayjs from 'dayjs';
import EmailLink from '~UI/EmailLink';
import WhitelabelTag from './WhitelabelTag';

export default function Layout(props: {
    brokerUuid: string;
    breadcrumb?: JSX.Element;
    children: JSX.Element | JSX.Element[];
    section: string;
}): ReactElement {
    const { breadcrumb, brokerUuid, children, section } = props;

    const [ approvalInProgress, setApprovalInProgress ] = useState<boolean>(false);
    const [ isRecordContactModalOpen, setIsRecordContactModalOpen ] = useState<boolean>(false);
    const [ isRejectModalOpen, setIsRejectModalOpen ] = useState<boolean>(false);

    const aggregators: IDictionary<IAggregator> = useSelector(aggregatorsSelector);
    const broker: IBroker = useSelector((state: IGlobalState) => brokerSelector(state, brokerUuid));

    const dispatch: Dispatch = useDispatch();

    useEffect(() => {
        if (!aggregators) {
            dispatch(aggregatorsListAction());
        }
    }, []);

    const showErrorsModal: (informationErrors: string[]) => void = useCallback((informationErrors: string[]) => {
        const missingInformationBlock: JSX.Element = informationErrors.length > 0 && (
            <>
                <p>Missing Information</p>
                <ul>
                    {informationErrors.map((error: string) => <li key={error}>{error}</li>)}
                </ul>
            </>
        );

        Modal.error({
            content: (
                <>
                    {missingInformationBlock}
                </>
            ),
            title: 'Unable to Proceed',
            width: 600,
        });
    }, []);

    const validateBroker: () => boolean = useCallback(() => {
        const informationErrors: string[] = [];

        if (!broker.insuranceExpiryDate) {
            informationErrors.push('Indemnity insurance expiry date required');
        } else if (dayjs() > dayjs(broker.insuranceExpiryDate)) {
            informationErrors.push('Indemnity insurance has expired');
        }

        if (!broker.photoIdExpiryDate) {
            informationErrors.push('Photo ID expiry date required');
        } else if (dayjs() > dayjs(broker.photoIdExpiryDate)) {
            informationErrors.push('Photo ID has expired');
        }

        if (!broker.membershipExpiryDate) {
            informationErrors.push('MFAA/FBAA/Industry body membership expiry date required');
        } else if (dayjs() > dayjs(broker.membershipExpiryDate)) {
            informationErrors.push('MFAA/FBAA/Industry body membership has expired');
        }

        if (!broker.afcaMembershipExpiryDate) {
            informationErrors.push('AFCA membership expiry date required');
        } else if (dayjs() > dayjs(broker.afcaMembershipExpiryDate)) {
            informationErrors.push('AFCA membership has expired');
        }

        if (!broker.asicSearchExpiryDate) {
            informationErrors.push('ACL/CR ASIC search expiry date required');
        } else if (dayjs() > dayjs(broker.asicSearchExpiryDate)) {
            informationErrors.push('ACL/CR ASIC search has expired');
        }

        if (informationErrors.length > 0) {
            showErrorsModal(informationErrors);
        }

        return !(informationErrors.length > 0);
    }, [
        broker,
        showErrorsModal,
    ]);

    const onClickApprove: () => void = useCallback(() => {
        const valid: boolean = validateBroker();

        if (valid) {
            setApprovalInProgress(true);
            dispatch(brokerApproveAction(broker.uuid, false));
        }
    }, [
        broker,
        dispatch,
        validateBroker,
    ]);

    const onClickApproveSendEmail: () => void = useCallback(() => {
        const valid: boolean = validateBroker();

        if (valid) {
            setApprovalInProgress(true);
            dispatch(brokerApproveAction(broker.uuid, true));
        }
    }, [
        broker,
        dispatch,
        validateBroker,
    ]);

    const onClickRecordContact: () => void = useCallback(() => {
        setIsRecordContactModalOpen(true);
    }, []);

    const onRecordContactCancel: () => void = useCallback(() => {
        setIsRecordContactModalOpen(false);
    }, []);

    const onClickReject: () => void = useCallback(() => {
        setIsRejectModalOpen(true);
    }, []);

    const onRejectCancel: () => void = useCallback(() => {
        setIsRejectModalOpen(false);
    }, []);

    const onClickResetStatus: () => void = useCallback(() => {
        setApprovalInProgress(false);

        dispatch(brokerResetApprovalStatusAction(brokerUuid));
    }, []);

    if (!broker || !aggregators) {
        return (
            <AntLayout className='broker'>
                <Spin />
            </AntLayout>
        );
    }

    const breadcrumbBlock: JSX.Element = breadcrumb ? (
        <>
            <Breadcrumb.Item><Link to={`/brokers/${broker.uuid}`}>{broker.firstName} {broker.lastName}</Link></Breadcrumb.Item>
            {breadcrumb}
        </>
    ) : (
        <Breadcrumb.Item>{broker.firstName} {broker.lastName}</Breadcrumb.Item>
    );

    const actionsMenu: MenuProps = {
        items: [
            {
                disabled: approvalInProgress,
                key: 'approve-email',
                label: 'Approve + Email',
                onClick: onClickApproveSendEmail,
            },
            {
                danger: true,
                disabled: approvalInProgress,
                key: 'reject',
                label: 'Reject',
                onClick: onClickReject,
            },
        ],
    };

    const extra: JSX.Element = (
        <Space>
            <Button type='default' disabled={approvalInProgress} onClick={onClickRecordContact}>Record Contact</Button>
            {broker.approvalStatus === ApprovalStatusEnum.Pending && <Dropdown.Button disabled={approvalInProgress} onClick={onClickApprove} menu={actionsMenu} type='primary'>Approve</Dropdown.Button>}
            {broker.approvalStatus !== ApprovalStatusEnum.Pending && <Button type='default' onClick={onClickResetStatus}>{broker.approvalStatus === ApprovalStatusEnum.Approved ? 'Unapprove' : 'Unreject'}</Button>}
        </Space>
    );

    const menuItems: ItemType[] = [
        {
            key: 'overview',
            label: <NavLink to={`/brokers/${broker.uuid}`}>Overview</NavLink>,
        },
        {
            key: 'edit',
            label: <NavLink to={`/brokers/${broker.uuid}/edit`}>Edit</NavLink>,
        },
        {
            key: 'deals',
            label: <NavLink to={`/brokers/${broker.uuid}/leads`}>Leads</NavLink>,
        },
        {
            key: 'applications',
            label: <NavLink to={`/brokers/${broker.uuid}/applications`}>Applications</NavLink>,
        },
        {
            key: 'documents',
            label: <NavLink to={`/brokers/${broker.uuid}/documents`}>Documents</NavLink>,
        },
        {
            key: 'notes',
            label: <NavLink to={`/brokers/${broker.uuid}/notes`}>Notes</NavLink>,
        },
        menuDivider,
        {
            key: 'history',
            label: <NavLink to={`/brokers/${broker.uuid}/history`}>History</NavLink>,
        },
    ];

    const subTitle: JSX.Element = (
        <Space>
            <span>{broker.code}</span>
            <WhitelabelTag broker={broker} />
        </Space>
    );

    return (
        <AntLayout className='broker'>
            <Breadcrumb className='breadcrumb'>
                <Breadcrumb.Item>Home</Breadcrumb.Item>
                <Breadcrumb.Item><Link to='/brokers'>Brokers</Link></Breadcrumb.Item>
                {breadcrumbBlock}
            </Breadcrumb>
            <PageHeader
                className='header'
                extra={extra}
                ghost={false}
                subTitle={subTitle}
                title={`${broker.firstName} ${broker.lastName}`}
            >
                <Descriptions size='small' column={4}>
                    <Descriptions.Item label='Phone'><a href={`tel:${broker.phone}`}>{broker.phone}</a></Descriptions.Item>
                    <Descriptions.Item label='Email'><EmailLink email={broker.email} /></Descriptions.Item>
                    <Descriptions.Item label='BDM'><BdmSelector broker={broker} /></Descriptions.Item>
                    {broker.linkedInUrl && <Descriptions.Item label='LinkedIn'><a href={broker.linkedInUrl} rel='noreferrer' target='_blank'>{broker.linkedInUrl}</a></Descriptions.Item>}
                    <Descriptions.Item label='Next Contact'><NextContact broker={broker} /></Descriptions.Item>
                    <Descriptions.Item label='Company'>{broker.companyName || '-'}</Descriptions.Item>
                    <Descriptions.Item label='Aggregator'>{broker.aggregatorUuid ? aggregators[broker.aggregatorUuid].name : '-'}</Descriptions.Item>
                    <Descriptions.Item label='Address'>{broker.postalAddress || '-'} {broker.suburb || '-'} {broker.state || '-'} {broker.postcode || '-'}</Descriptions.Item>
                </Descriptions>
            </PageHeader>
            <AntLayout className='content-wrapper'>
                <AntLayout.Sider className='menu' theme='light' width={240}>
                    <Menu
                        defaultSelectedKeys={[ section ]}
                        items={menuItems}
                        mode='inline'
                    />
                </AntLayout.Sider>
                <AntLayout.Content className={`content ${section}`}>
                    {children}
                </AntLayout.Content>
            </AntLayout>
            <RecordContactModal
                broker={broker}
                isOpen={isRecordContactModalOpen}
                onCancel={onRecordContactCancel}
            />
            <RejectModal
                broker={broker}
                isOpen={isRejectModalOpen}
                onCancel={onRejectCancel}
            />
        </AntLayout>
    );
}
